import React from 'react';
import { Box, Skeleton, Grid, Typography } from '@mui/material';
import { ElectricBolt as ElectricIcon } from '@mui/icons-material';

// Chart skeleton
export function ChartSkeleton({ height = 300 }) {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Skeleton
        variant="text"
        width={200}
        sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 1 }}
      />
      <Skeleton
        variant="text"
        width={150}
        sx={{ bgcolor: 'rgba(255,255,255,0.05)', mb: 2 }}
      />
      <Skeleton
        variant="rectangular"
        height={height - 80}
        sx={{ bgcolor: 'rgba(255,255,255,0.05)', borderRadius: 1 }}
      />
    </Box>
  );
}

// Stat card skeleton
export function StatCardSkeleton() {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      <Skeleton
        variant="text"
        width="60%"
        sx={{ bgcolor: 'rgba(255,255,255,0.1)', mb: 1 }}
      />
      <Skeleton
        variant="text"
        width="40%"
        height={40}
        sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
      />
    </Box>
  );
}

// Table skeleton
export function TableSkeleton({ rows = 5 }) {
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(255,255,255,0.02)',
        borderRadius: 2,
        p: 2,
        border: '1px solid rgba(255,255,255,0.1)',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, pb: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        {[1, 2, 3, 4].map((i) => (
          <Skeleton
            key={i}
            variant="text"
            width={`${Math.random() * 20 + 15}%`}
            sx={{ bgcolor: 'rgba(255,255,255,0.15)' }}
          />
        ))}
      </Box>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton
              key={i}
              variant="text"
              width={`${Math.random() * 20 + 15}%`}
              sx={{ bgcolor: 'rgba(255,255,255,0.05)' }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

// Dashboard skeleton (combines multiple)
export function DashboardSkeleton() {
  return (
    <Box>
      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <Grid item xs={6} md={2} key={i}>
            <StatCardSkeleton />
          </Grid>
        ))}
      </Grid>

      {/* Charts row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <ChartSkeleton height={350} />
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartSkeleton height={350} />
        </Grid>
      </Grid>

      {/* Table */}
      <TableSkeleton rows={6} />
    </Box>
  );
}

// Page loading skeleton with branded animation
export function PageSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3,
      }}
    >
      {/* Animated Logo */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
        }}
      >
        {/* Outer glow ring */}
        <Box
          sx={{
            position: 'absolute',
            width: 100,
            height: 100,
            borderRadius: '50%',
            border: '2px solid transparent',
            borderTopColor: '#00d4ff',
            borderRightColor: '#00ff88',
            animation: 'spin 1.5s linear infinite',
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
          }}
        />
        {/* Inner glow ring */}
        <Box
          sx={{
            position: 'absolute',
            width: 80,
            height: 80,
            borderRadius: '50%',
            border: '2px solid transparent',
            borderBottomColor: '#00d4ff',
            borderLeftColor: '#00ff88',
            animation: 'spinReverse 1s linear infinite',
            '@keyframes spinReverse': {
              from: { transform: 'rotate(360deg)' },
              to: { transform: 'rotate(0deg)' },
            },
          }}
        />
        {/* Icon */}
        <ElectricIcon
          sx={{
            fontSize: 40,
            color: '#00d4ff',
            animation: 'pulse 1.5s ease-in-out infinite',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1, transform: 'scale(1)' },
              '50%': { opacity: 0.7, transform: 'scale(0.95)' },
            },
          }}
        />
      </Box>

      {/* Brand Name */}
      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 1,
        }}
      >
        EnergyCosmos
      </Typography>

      {/* Loading text */}
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.5)',
          animation: 'fadeInOut 1.5s ease-in-out infinite',
          '@keyframes fadeInOut': {
            '0%, 100%': { opacity: 0.5 },
            '50%': { opacity: 1 },
          },
        }}
      >
        Loading market data...
      </Typography>

      {/* Progress bar */}
      <Box
        sx={{
          mt: 3,
          width: 200,
          height: 3,
          borderRadius: 2,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: '40%',
            height: '100%',
            borderRadius: 2,
            background: 'linear-gradient(90deg, #00d4ff, #00ff88)',
            animation: 'loading 1.5s ease-in-out infinite',
            '@keyframes loading': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(350%)' },
            },
          }}
        />
      </Box>
    </Box>
  );
}

export default {
  ChartSkeleton,
  StatCardSkeleton,
  TableSkeleton,
  DashboardSkeleton,
  PageSkeleton,
};
